import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { ServiceProvider } from '@/services/providerService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ProviderCardProps {
  provider: ServiceProvider;
}

export const ProviderCard: React.FC<ProviderCardProps> = ({ provider }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getOpenStatus = () => {
    const now = new Date();
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
    const daySchedule = provider.schedule[dayOfWeek];
    
    if (!daySchedule || daySchedule.closed) {
      return { isOpen: false, closeTime: null };
    }
    
    const currentTime = now.toTimeString().slice(0, 5);
    const isOpen = currentTime >= daySchedule.open! && currentTime <= daySchedule.close!;
    
    return { isOpen, closeTime: isOpen ? daySchedule.close : null };
  };

  const { isOpen, closeTime } = getOpenStatus();

  return (
    <Card 
      className="p-4 cursor-pointer hover:shadow-lg transition-shadow bg-card"
      onClick={() => navigate(`/services/provider/${provider.id}`)}
    >
      <div className="flex gap-4">
        <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          {provider.photos && provider.photos.length > 0 ? (
            <img 
              src={provider.photos[0]} 
              alt={provider.business_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              📷
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg truncate text-foreground">{provider.business_name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {provider.distance_km !== undefined 
                    ? `${provider.distance_km.toFixed(1)} km` 
                    : provider.city || provider.address}
                </span>
              </div>
            </div>

            {provider.verified && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 flex-shrink-0">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('services.verified')}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="ml-1 font-medium text-foreground">{provider.rating.toFixed(1)}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              ({provider.total_reviews} {t('services.reviews')})
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className={`text-sm font-medium ${isOpen ? 'text-green-600' : 'text-red-600'}`}>
                {isOpen ? t('services.openNow') : t('services.closed')}
              </span>
              {isOpen && closeTime && (
                <span className="text-xs text-muted-foreground ml-1">
                  • {t('services.closesAt')} {closeTime}
                </span>
              )}
            </div>
            {provider.schedule.emergency_available && (
              <Badge variant="destructive" className="text-xs h-5 gap-1">
                <AlertCircle className="w-3 h-3" />
                {t('services.emergencyService')}
              </Badge>
            )}
          </div>

          {provider.services && provider.services.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {provider.services.slice(0, 3).map((service, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {service}
                </Badge>
              ))}
              {provider.services.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{provider.services.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
